import React, { createContext, useCallback, useContext, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PaywallContext = createContext(null);

export function PaywallProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("This feature requires HCRIS+ Pro.");

  const show = useCallback((msg) => {
    if (msg) setMessage(msg);
    setOpen(true);
  }, []);

  const subscribe = async () => {
    try {
      const r = await axios.post(`${API}/billing/checkout`, {}, { withCredentials: true });
      window.location.href = r.data?.checkout_url;
    } catch (e) {
      // fall through to /pricing
      window.location.href = "/pricing";
    }
  };

  // Attach a single axios interceptor that surfaces 402s
  React.useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 402) {
          const d = err.response.data?.detail || {};
          show(d.message || "Subscription required");
        }
        return Promise.reject(err);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, [show]);

  return (
    <PaywallContext.Provider value={{ show }}>
      {children}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
             data-testid="paywall-modal" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase font-mono text-emerald-700 tracking-wider">HCRIS+ Pro</div>
            <h2 className="mt-1 text-xl font-semibold text-zinc-900">Subscribe to unlock</h2>
            <p className="mt-2 text-sm text-zinc-700">{message}</p>
            <div className="mt-4 text-sm text-zinc-600">
              $99/mo · Unlimited LLM reports, shareable scorecards, scorecard PDFs.
              Cancel anytime.
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setOpen(false)}
                      className="px-3 py-2 rounded text-sm font-mono text-zinc-700 hover:bg-zinc-100">
                Maybe later
              </button>
              <button onClick={subscribe}
                      className="px-4 py-2 rounded bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-mono uppercase tracking-wider"
                      data-testid="paywall-subscribe-btn">
                Subscribe · $99/mo
              </button>
            </div>
          </div>
        </div>
      )}
    </PaywallContext.Provider>
  );
}

export function usePaywall() {
  return useContext(PaywallContext) || { show: () => {} };
}
